import { SessionFilters } from 'components/table/SessionFilters';
import { FilterChip } from 'components/table/TableFilters';
import { DateFormat } from 'constants/DateFormat';
import dayjs from 'dayjs';
import { IGame, IOrganization } from 'interfaces/api/IGame';
import { action, makeAutoObservable, runInAction } from 'mobx';
import { GamesService } from 'services/data-services/GamesService';
import { LogService } from 'services/LogService';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { ISort } from 'constants/ISort';
import lodash from 'lodash';
import { ExceptionType } from 'services/Result';

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

const DEFAULT_COLUMN = 'field';
const DEFAULT_DIRECTION = 'asc';
class SessionStore {
  isLoading = false;

  gameApiService: GamesService;

  logService: LogService;

  sessions: IGame[] = [];

  cachedSessions: IGame[] = [];

  organization: IOrganization = { name: '' };

  showError = false;

  filters = [] as FilterChip[];

  sort: ISort;

  useSharingSharingNetworkRules = false;

  missingOrganizationError = false;

  constructor(gameApiService: GamesService, logService: LogService) {
    this.gameApiService = gameApiService;
    this.logService = logService;
    this.sort = {
      column: DEFAULT_COLUMN,
      direction: DEFAULT_DIRECTION,
    };

    const savedSharingNetwork = JSON.parse(localStorage.getItem('sharingNetwork'));
    if (savedSharingNetwork === true || savedSharingNetwork === false) {
      this.useSharingSharingNetworkRules = savedSharingNetwork;
    }

    makeAutoObservable(this);
  }

  setUseSharingSharingNetworkRules = (value: boolean) => {
    localStorage.setItem('sharingNetwork', `${value}`);
    this.useSharingSharingNetworkRules = value;
  };

  fetchData = async (sharingNetwork: boolean): Promise<void> => {
    this.isLoading = true;
    this.showError = false;
    try {
      const currentGames = await this.gameApiService.getCurrentGames(sharingNetwork);
      runInAction(() => {
        this.sessions = currentGames.games;
        this.sessions = this.cachedSessions = currentGames.games;
        this.organization = currentGames.organization;
      });
    } catch (error) {
      if (error.cause && error.cause === ExceptionType.MissingOrganizationException) {
        this.missingOrganizationError = true;
        this.logService.logError({ error, message: `Missing organization exception` });
        return;
      }
      this.logService.logError({ error, message: `When getting current games` });
      this.showError = true;
    } finally {
      runInAction(() => (this.isLoading = false));
    }
  };

  setLoading = (value: boolean): void => {
    this.isLoading = value;
  };

  setSortingDirection = (columnKey: string): 'asc' | 'desc' | boolean => {
    if (columnKey !== this.sort.column) return 'asc';
    if (this.sort.direction === 'asc') return 'desc';
    if (this.sort.direction === 'desc') return false;

    return 'asc';
  };

  changeSortOrderBy = (columnKey: string): void => {
    let sort = this.setSortingDirection(columnKey);

    if (!sort) {
      columnKey = DEFAULT_COLUMN;
      sort = DEFAULT_DIRECTION;
    }

    this.sessions = lodash.orderBy(this.sessions, [columnKey, DEFAULT_COLUMN], sort);

    if (sort !== true) {
      this.sort = {
        column: columnKey,
        direction: sort,
      };
    }
  };

  addFilter = (value: FilterChip): void => {
    this.filters.forEach((filter, index) => {
      if (filter.id === value.id) {
        this.filters.splice(index, 1);
      }
    });

    this.filters.push(value);
    this.filterSessions();
  };

  removeFilter = (id: string): void => {
    this.sessions = this.cachedSessions;
    this.filters.splice(this.filters.indexOf(this.filters.find((filter) => filter.id === id) as any), 1);
    if (this.filters.length > 0) {
      this.setLoading(true);
      this.filterSessions();
      this.setLoading(false);
    }
  };

  filterSessions = (): void => {
    this.sessions = this.cachedSessions;
    const filterResult = this.sessions.filter((session: IGame): boolean => {
      const tempArr: boolean[] = this.findMatchingFilters(session);

      return tempArr.every((item: boolean) => item === true);
    });

    this.sessions = filterResult;
  };

  findMatchingFilters = (session: IGame): boolean[] => {
    const tempArr: boolean[] = [];
    this.filters
      .filter((filter) => filter.active)
      .forEach((filter: FilterChip) => {
        switch (filter.id) {
          case SessionFilters.GameDate: {
            const splittedDate = filter.value.split(' ');
            const sessionDate = dayjs((session as any)[filter.id]).format(DateFormat.queryFilterFormat);
            return tempArr.push(dayjs(splittedDate[0]).isSameOrBefore(sessionDate) && dayjs(splittedDate[1]).isSameOrAfter(sessionDate));
          }
          case SessionFilters.Venue:
          case SessionFilters.Field:
          case SessionFilters.HomeTeam:
          case SessionFilters.AwayTeam: {
            return tempArr.push(session[filter.id].toLowerCase().indexOf(filter.value.toLowerCase()) > -1);
          }
        }
      });

    return tempArr;
  };

  getFilter = (filter: FilterChip) => {
    return this.filters[this.filters.indexOf(filter)];
  };

  pinFilter = (filter: FilterChip) => {
    this.getFilter(filter).pinned = true;
  };

  unpinFilter = (filter: FilterChip) => {
    this.getFilter(filter).pinned = false;
  };

  disableFilter = (filter: FilterChip) => {
    this.getFilter(filter).active = false;
    this.filterSessions();
  };

  activeFilter = (filter: FilterChip) => {
    this.getFilter(filter).active = true;
    this.filterSessions();
  };
}

export default SessionStore;
